import $ from 'jquery';
import 'jquery.ui.widget';
import 'jquery.ui.mouse';
import 'jquery.ui.core';
import 'jquery.ui.draggable';
import 'jquery.ui.sortable';
// Above jquery import order is specific - don't change without verifying.

/**
 * A component that controls the interaction between a number of related ImageField components
 *
 * @param {Object} options
 * @param {Array.<Imagefield>} options.imageFields
 * @param {jQuery} options.$el
 */
class ImageFieldSet {
    constructor(options = {}) {
        this.imageFields = options.imageFields ?? [];
        this.$el = options.$el ?? null;
        this.numImagesInFlight = 0;
        this.emptySlotSelector = '.product-image-empty-slot';

        this.bind();
    }

    /**
     * Disables the image fields so they cannot accept user input
     */
    disable() {
        this.$el.addClass('disabled');
        this.$el.sortable('option', 'disabled', true);

        this.imageFields.forEach((field) => {
            field.disable();
        });
    }

    /**
     * Enables the image fields so they can accept user input
     */
    enable() {
        this.$el.removeClass('disabled');
        this.$el.sortable('option', 'disabled', false);

        this.imageFields.forEach((field) => {
            field.enable();
        });
    }

    /**
     * @returns {jQuery} The jQuery instance representing the ImageFieldSet
     */
    getEl() {
        return this.$el;
    }

    /**
     * @returns {Array.<string>} A collection of image guids that have been uploaded to the various ImageFields
     */
    getValue() {
        const val = [];
        this.imageFields.forEach((field) => {
            const data = field.getImageData();
            if (data) {
                const dataObj = { value: data.value };

                const h = data.height;
                if (h) {
                    dataObj.height = h;
                }

                const w = data.width;
                if (w) {
                    dataObj.width = w;
                }

                val.push(dataObj);
            }
        });
        return val;
    }

    /**
     * Sets the "Primary" label on the first ImageField with an image and removes all other "Primary" labels.
     */
    setPrimaryImage() {
        this.imageFields.forEach((field) => {
            field.removePrimaryLabel();
        });

        const primaryField = this.imageFields.find((field) => field.hasImage());
        if (primaryField) {
            primaryField.addPrimaryLabel();
        }
    }

    /**
     * Sets the values for the ImageFields sequentially; does not maintain order.
     *
     * @param {Array.<ImageData>} imageDataList Collection of ImageData to set on each ImageField
     */
    setValues(imageDataList) {
        if (imageDataList) {
            this.imageFields.forEach((imageField, i) => {
                let val = null;
                if (i < imageDataList.length) {
                    val = imageDataList[i];
                }

                imageField.setImageData(val);

                if (val) {
                    imageField.displayImage();
                } else {
                    imageField.removeImage();
                }
            });
        }
    }

    /**
     * Sorts the given array of ImageFields to match their order in the DOM.
     *
     * @param {Array.<ImageField>} imageFields - The array of ImageField instances to sort.
     */
    sortImageFieldsByDomOrder(imageFields) {
        imageFields.sort((a, b) => {
            const index1 = a.getEl().index();
            const index2 = b.getEl().index();
            return index1 - index2;
        });
    }

    /**
     * Binds actions to the various elements that comprise the ImageFieldSet
     */
    bind() {
        this.imageFields.forEach((imageField) => {
            const $el = imageField.getEl();

            // if multiple images are being sent up (i.e. they were dragged to a dropzone)
            // we need to keep track of how many images are still being processed so we can
            // send up one event to the server with all of their guids...
            $el.on(
                [
                    'imagefield:imageadded',
                    'imagefield:imagenotadded',
                    'imagefield:imageuploadadd',
                    'imagefield:imageuploadfail',
                ].join(' '),
                (e) => {
                    this.numImagesInFlight += e.type === 'imagefield:imageuploadadd' ? 1 : -1;
                },
            );

            // if an image was uploaded but not added to a given slot because multiple were
            // uploaded at once, we need to try to find an open slot for them, or display an
            // error if there isn't one...
            $el.on('imagefield:imagenotadded', (e, data) => {
                const emptyImageField = this.getEmptyImageField();
                if (!emptyImageField) {
                    this.trigger('imagefieldset:imageuploadfail', {
                        errors: [
                            {
                                // todo (CHAIR-5561) extract this from JS
                                display: `You can only upload ${this.imageFields.length} images at most`,
                            },
                        ],
                    });
                } else {
                    emptyImageField.setImageData(data, true);
                    emptyImageField.displayImage();
                }
            });

            $el.on('imagefield:imageuploadfail', (e, data) => {
                this.trigger('imagefieldset:imageuploadfail', data);
            });

            // indicate to listeners that some number of images have been added...
            $el.on(['imagefield:imagedatachanged', 'imagefield:imageremoved'].join(' '), () => {
                if (this.numImagesInFlight === 0) {
                    this.sortImageFieldsByDomOrder(this.imageFields);
                    this.setPrimaryImage();
                    this.trigger('imagefieldset:imagedatachanged', { guids: this.getValue() });
                }
            });
        });

        this.$el.sortable({
            cancel: this.emptySlotSelector,
            activate: () => {
                this.$el.addClass('sort-in-progress');
            },
            deactivate: () => {
                this.$el.removeClass('sort-in-progress');
            },
            update: () => {
                this.sortImageFieldsByDomOrder(this.imageFields);

                this.$el.find(this.emptySlotSelector).appendTo(this.$el);

                if (this.imageFields.length > 0) {
                    this.setPrimaryImage();
                }

                this.trigger('imagefieldset:imagedatachanged', { guids: this.getValue() });
            },
            helper: 'clone',
        });
    }

    /**
     * @returns {ImageField|null} The first available instance of an ImageField that does not contain an image, or null if none exist
     */
    getEmptyImageField() {
        let result = null;
        this.imageFields.forEach((imageField) => {
            if (!imageField.hasImage() && !result) {
                result = imageField;
            }
        });
        return result;
    }

    /**
     * Triggers a new event while passing along any other additional data provided
     *
     * @param {string} type The new event's type
     * @param {Object=} optData Additional data to be passed along as an argument to the event handler
     */
    trigger(type, optData) {
        this.getEl().trigger($.Event(type), optData);
    }
}

export default ImageFieldSet;
